import { useState } from 'react';
import { PillTheme } from './pill-theme';
import ReactDOM from 'react-dom';

interface PillProps {
  children: JSX.Element | JSX.Element[];
  theme: PillTheme;
  classOverrides?: string;
  tooltipText?: string;
}

function getClasses(theme: PillTheme): string {
  switch (theme) {
    case PillTheme.SUCCESS:
      return 'bg-success-50 border border-success-400 text-success-400';
    case PillTheme.ERROR:
      return 'bg-error-75 border border-error-300 text-error-300';
    case PillTheme.DISABLED:
      return 'bg-neutral-150 border border-neutral-400 text-neutral-400';
    case PillTheme.PROCESSING:
      return 'bg-primary-50 border border-primary-400 text-primary-400';
    case PillTheme.SCHEDULED:
      return 'bg-secondary-50 border border-secondary-400 text-secondary-400';
    case PillTheme.WAITING:
      return 'bg-warning-100 border border-warning-600 text-warning-600';
    default:
      throw new Error(`Unable to implement pill with theme of type: ${theme}`);
  }
}

export default function Pill({
  children,
  theme,
  classOverrides,
  tooltipText
}: PillProps) {
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleMouseOver = (event: React.MouseEvent) => {
    const target = event.currentTarget.getBoundingClientRect();
    setPosition({
      top: target.bottom + window.scrollY + 5,
      left: target.left + window.scrollX + target.width / 2
    });
  };

  const handleMouseOut = () => {
    setPosition(null);
  };

  const classes = classOverrides ?? 'px-2 flex justify-center items-center';
  if (theme === PillTheme.ERROR) {
    return (
      <>
        <div
          className={`${getClasses(
            theme
          )} ${classes} rounded transition-all group cursor-pointer`}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          {children}
        </div>

        {position &&
          tooltipText &&
          ReactDOM.createPortal(
            <div
              className="fixed z-50 p-2 text-xs bg-error-75 border border-error-300 rounded text-error-300 shadow-lg"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap'
              }}
            >
              {tooltipText}
            </div>,
            document.body
          )}
      </>
    );
  } else {
    return (
      <div className={`${getClasses(theme)} ${classes} rounded transition-all`}>
        {children}
      </div>
    );
  }
}
